import { useState, useEffect, useCallback } from 'react';
import { socket, AuthRequiredEvent } from '../socket'
import { Auth } from '../peregrineFlowInterfaces';
import { trackAuth } from '../analytics';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Typography,
    Alert,
    Box,
    Divider,
    Link,
    CircularProgress
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import useStore from '../store';
import Dialog from '@mui/material/Dialog';
import {
    isFirebaseEnabled,
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
} from './firebaseConfig';

export type OnAuthRequired = (event: AuthRequiredEvent) => void;

type AuthManagerProps = {
    onAuthRequired?: OnAuthRequired
}

type AuthMode = 'login' | 'signup';

const AuthManager = ({ onAuthRequired }: AuthManagerProps) => {
    const [emailOrUsername, setEmailOrUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('Invalid credentials');
    const [authRequiredMessage, setAuthRequiredMessage] = useState<string | null>(null);
    const [authMode, setAuthMode] = useState<AuthMode>('login');
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    const showLoginDialogState = useStore((state) => state.showLoginDialog);
    const setShowLoginDialogState = useStore((state) => state.setShowLoginDialog);
    const setUsernameInStore = useStore((state) => state.setUsername);
    const setToken = useStore((state) => state.setToken);
    const setIsConnected = useStore((state) => state.setConnected);
    const setIsGuest = useStore((state) => state.setIsGuest);
    const setIsAuthenticated = useStore((state) => state.setIsAuthenticated);

    const handleAuthSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setShowError(false);
        setIsLoading(true);

        if (authMode === 'signup') {
            // Sign-up always uses Firebase
            await handleFirebaseSignUp();
        } else {
            // Login tries Firebase first, then falls back to local/LDAP
            await handleLogin();
        }
        // Note: loading state is cleared by socket response handler or error handlers
    };

    const handleFirebaseSignUp = async () => {
        if (!isFirebaseEnabled()) {
            setErrorMessage('Firebase is not configured. Cannot create new accounts.');
            setShowError(true);
            setIsLoading(false);
            return;
        }

        try {
            await signUpWithEmail(emailOrUsername, password);

            // Show success message - user must verify email before logging in
            setErrorMessage('');
            setShowError(false);
            setSuccessMessage(`Account created successfully! Please check your email (${emailOrUsername}) to verify your account before logging in.`);
            setShowSuccessMessage(true);

            // Track successful signup
            trackAuth('signup', 'firebase', true);

            // Switch to login mode
            setAuthMode('login');
            setEmailOrUsername('');
            setPassword('');
            setIsLoading(false);

        } catch (error: any) {
            console.error('Firebase sign-up error:', error);
            let message = 'Sign-up failed';

            if (error.code === 'auth/email-already-in-use') {
                message = 'Email already in use. Please login instead.';
            } else if (error.code === 'auth/weak-password') {
                message = 'Password should be at least 6 characters.';
            } else if (error.code === 'auth/invalid-email') {
                message = 'Invalid email address.';
            } else if (error.message) {
                message = error.message;
            }

            // Track failed signup
            trackAuth('signup', 'firebase', false);

            setErrorMessage(message);
            setShowError(true);
            setIsLoading(false);
        }
    };

    const handleLogin = async () => {
        // Option A: Try Firebase first (if enabled and input looks like email),
        // then fall back to local/LDAP chain

        const looksLikeEmail = emailOrUsername.includes('@');

        if (isFirebaseEnabled() && looksLikeEmail) {
            try {
                // Try Firebase authentication
                const userCredential = await signInWithEmail(emailOrUsername, password);

                // Check if email is verified
                if (!userCredential.user.emailVerified) {
                    setErrorMessage('Please verify your email address before logging in. Check your inbox for the verification link.');
                    setShowError(true);
                    setIsLoading(false);
                    return;
                }

                const idToken = await userCredential.user.getIdToken();

                // Send to backend
                const auth: Auth = {
                    firebase_token: idToken,
                    username: emailOrUsername,
                };
                socket.emit("auth", auth);
                // Don't clear loading here - wait for backend response
                return; // Success, don't fall back

            } catch (error: any) {
                console.log('Firebase login failed, falling back to local/LDAP:', error.code);

                // Only fall back to local/LDAP for specific errors
                if (error.code === 'auth/user-not-found' ||
                    error.code === 'auth/wrong-password' ||
                    error.code === 'auth/invalid-credential') {
                    // Fall through to try local/LDAP
                } else {
                    // Other Firebase errors (network, etc.) - show error
                    let message = 'Authentication failed';
                    if (error.code === 'auth/invalid-email') {
                        message = 'Invalid email address.';
                    } else if (error.message) {
                        message = error.message;
                    }
                    setErrorMessage(message);
                    setShowError(true);
                    setIsLoading(false);
                    return;
                }
            }
        }

        // Try local/LDAP authentication chain
        const auth: Auth = {
            username: emailOrUsername,
            password: password,
            token: undefined,
        };
        socket.emit("auth", auth);
        // Don't clear loading here - wait for backend response
    };

    const handleSocialLogin = async (provider: 'google') => {
        setShowError(false);
        setIsLoading(true);

        try {
            let userCredential;

            if (provider === 'google') {
                userCredential = await signInWithGoogle();
            }
            else {
                throw new Error('Unsupported provider');
            }

            // Get Firebase ID token
            const idToken = await userCredential.user.getIdToken();

            // Send to backend
            const auth: Auth = {
                firebase_token: idToken,
                username: userCredential.user.email || userCredential.user.uid,
            };
            socket.emit("auth", auth);

        } catch (error: any) {
            console.error(`${provider} login error:`, error);
            let message = `${provider} authentication failed`;

            if (error.code === 'auth/popup-closed-by-user') {
                message = 'Login cancelled';
            } else if (error.message) {
                message = error.message;
            }

            setErrorMessage(message);
            setShowError(true);
            setIsLoading(false);
        }
    };

    const closeLoginDialog = () => {
        setShowLoginDialogState(false);
        setAuthRequiredMessage(null);
        setShowError(false);
        setErrorMessage('Invalid credentials');
    };

    const toggleAuthMode = () => {
        setAuthMode(authMode === 'login' ? 'signup' : 'login');
        setShowError(false);
        setShowSuccessMessage(false);
        setSuccessMessage('');
    };

    useEffect(() => {
        const onConnected = (data: any) => {
            setIsConnected(true);
            // if token exists reauthenticate
            const token = sessionStorage.getItem('auth_token');
            if (token) {
                const auth: Auth = { username: undefined, password: undefined, token: token };
                socket.emit("auth", auth);
            }
            // No token means we're in guest mode (server creates guest session automatically)
            setIsGuest(data.is_guest === true);
            setUsernameInStore(data.username);
        }

        const onDisconnect = () => {
            setIsConnected(false);
        }

        const onAuth = (data: any) => {
            if (data.success && data.token) {
                sessionStorage.setItem('auth_token', data.token);
                setToken(data.token);
                setUsernameInStore(data.username);
                setIsAuthenticated(true);
                setIsGuest(data.is_guest === true);
                setShowError(false);

                trackAuth('login', data.username);

                // Keep loading state visible for a moment before closing dialog
                setTimeout(() => {
                    setIsLoading(false);
                    setShowLoginDialogState(false);
                    setAuthRequiredMessage(null);
                }, 500);
            }
            else {
                setIsLoading(false);
                setShowError(true);
                setErrorMessage('Authentication failed');
            }
        }

        const onLogout = () => {
            sessionStorage.removeItem('auth_token');
            setToken(undefined);
            setIsGuest(true);
            setIsAuthenticated(false);
            window.location.reload();
        }

        const onAuthRequiredEvent = (data: AuthRequiredEvent) => {
            // Show login dialog when guest tries to perform restricted operation
            setAuthRequiredMessage(data.message);
            setShowLoginDialogState(true);
            onAuthRequired?.(data);
        }

        socket.on('auth', onAuth);
        socket.on('logout', onLogout);
        socket.on('auth_required', onAuthRequiredEvent);
        socket.on('connected', onConnected);
        socket.on('disconnect', onDisconnect);
        return () => {
            socket.off('auth', onAuth);
            socket.off('logout', onLogout);
            socket.off('auth_required', onAuthRequiredEvent);
            socket.off('connected', onConnected);
            socket.off('disconnect', onDisconnect);
        };
    }, [onAuthRequired, setIsConnected, setToken, setUsernameInStore, setIsGuest, setIsAuthenticated, setShowLoginDialogState]);

    const unifiedAuthForm = (
        <>
            <form onSubmit={handleAuthSubmit}>
                <TextField
                    label={authMode === 'signup' ? 'Email' : 'Email or Username'}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    type={authMode === 'signup' ? 'email' : 'text'}
                    value={emailOrUsername}
                    onChange={e => setEmailOrUsername(e.target.value)}
                    required
                    helperText={authMode === 'signup' ? 'Use your email address to create an account' : 'Enter email for Firebase accounts or username for local/LDAP'}
                />
                <TextField
                    label="Password"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    helperText={authMode === 'signup' ? 'Password must be at least 6 characters' : ''}
                />
                {showSuccessMessage && <Alert severity="success" sx={{ mt: 2, mb: 2 }}>{successMessage}</Alert>}
                {showError && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{errorMessage}</Alert>}
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    sx={{ mt: 3, width: '100%' }}
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : undefined}
                >
                    {isLoading ? 'Please wait...' : (authMode === 'signup' ? 'Sign Up' : 'Login')}
                </Button>
            </form>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                        e.preventDefault();
                        toggleAuthMode();
                    }}
                    sx={{ cursor: 'pointer' }}
                >
                    {authMode === 'login'
                        ? "Don't have an account? Sign up"
                        : 'Already have an account? Login'}
                </Link>
            </Box>

            {isFirebaseEnabled() && (
                <>
                    <Divider sx={{ my: 3 }}>OR</Divider>

                    <Button
                        variant="outlined"
                        startIcon={isLoading ? <CircularProgress size={20} color="primary" /> : <GoogleIcon />}
                        onClick={() => handleSocialLogin('google')}
                        sx={{ mb: 1, width: '100%' }}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Please wait...' : 'Continue with Google'}
                    </Button>
                </>
            )}
        </>
    );

    const loginForm = (
        <Card>
            <CardHeader
                title={<Typography align="center" variant="h6">
                    {authMode === 'signup' ? 'Sign Up' : 'Login'}
                </Typography>}
            />
            <CardContent sx={{ position: 'relative' }}>
                {authRequiredMessage && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {authRequiredMessage}
                    </Alert>
                )}

                {unifiedAuthForm}

                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 10,
                            gap: 2
                        }}
                    >
                        <CircularProgress size={40} />
                        <Typography variant="body1" color="textSecondary">
                            Authenticating...
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );

    return (
        <Dialog open={showLoginDialogState} onClose={closeLoginDialog}>
            {loginForm}
        </Dialog>
    );
};

export default AuthManager;

// Helper hooks for components that need auth actions
export const useAuth = () => {
    const isAuthenticated = useStore((state) => state.isAuthenticated);
    const isGuest = useStore((state) => state.isGuest);
    const setShowLoginDialog = useStore((state) => state.setShowLoginDialog);

    const logout = useCallback(() => {
        trackAuth('logout');
        socket.emit("logout");
    }, []);

    const showLoginDialog = useCallback(() => {
        setShowLoginDialog(true);
    }, [setShowLoginDialog]);

    return {
        isAuthenticated,
        isGuest,
        logout: isAuthenticated ? logout : undefined,
        showLoginDialog,
    };
};
